import type { Fetcher } from 'swr';
import useSWR from 'swr';

import { carsRetrieve } from '~/data/generated/endpoints/cars/cars';
import type { Car } from '~/data/generated/model';
import { CarsRetrieveResponse } from '~/data/generated/zod/cars/cars.zod';
import { parseApiResponse } from '~/data/parseApiResponse';

// AMP-593: parses against the GENERATED `CarsRetrieveResponse` — NOT against
// `~/data/schemas/car`'s `carSchema`, which this file does not import and which
// currently has no production consumer at all. (`carSchema` is not a drop-in
// for the generated schema either: it re-types `property` as a lazy, optional
// `propertySchema`.) The generated schema declares every field the `Car` model
// does, so nothing this hook returns is stripped.
//
// AMP-619 fixed the base's known inaccuracies at source, so the mismatches this
// note used to list are gone: `serviceProviderInternalId` is `allow_null`,
// `property.portfolio` emits real null instead of a degenerate `{name: ""}`, and
// `latestVitals`/`lastTripDt`/`lastPingDt` left the API surface with the IoT
// divestiture. The boundary stays report-only per `apiBoundaryConfig`: flipping
// a domain strict is gated on Sentry evidence over a window plus a named owner
// (AMP-623), not on the base being accurate alone.
const carFetcher: Fetcher<Car, readonly ['car', carId: string]> = async ([_, carId]) => {
  const { data } = await carsRetrieve(carId);
  return parseApiResponse(CarsRetrieveResponse, data, 'Car');
};

export default function useCar(carId: string | undefined) {
  const {
    data,
    error,
    isLoading: loading,
  } = useSWR(carId ? (['car', carId] as const) : null, carFetcher);

  return { car: data, error, loading };
}
